import React, { useState, useEffect, useRef } from 'react';
import { ref, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { storage, functions } from '../config/firebase';
import { MicrophoneIcon, PlayIcon, StopIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  STAGES,
  INTENSITY_STOPS,
  paramsForStage,
  percentForStage,
} from '../utils/audioEnhancementPresets';

// Shared audio enhancement profile editor — used by both the Super Admin
// per-user tool and every user's own Settings > Audio Enhancement drawer.
// One implementation means the "simple" preset dropdowns and the "advanced"
// raw fields can never drift out of sync between the two surfaces, same as
// within a single page: everything here reads/writes the same profile object.

// Mirrors the hardcoded chain in functions/src/index.ts::DEFAULT_ENHANCEMENT_PROFILE
// exactly — this is what every user gets today with no profile saved yet.
export const DEFAULT_AUDIO_PROFILE = {
  denoise: { enabled: false, nr: 12 },
  highpass: { enabled: true, freq: 80 },
  equalizer: {
    enabled: false,
    bands: [
      { freq: 80, width: 100, gain: 0 }, // rumble / mud
      { freq: 200, width: 150, gain: 0 }, // boominess
      { freq: 2500, width: 1000, gain: 0 }, // clarity / intelligibility
      { freq: 6500, width: 1500, gain: 0 }, // sibilance
    ],
  },
  loudnorm: { enabled: true, targetLUFS: -16, truePeak: -1.5, range: 11 },
  compand: {
    enabled: true,
    attack: 0.1,
    decay: 0.2,
    points: '-80/-900|-45/-15|-27/-9|0/-7|20/-7',
  },
};

// Voice-type presets for the graphic equalizer — starting points for the four
// bands (rumble, boominess, clarity, sibilance), not scientifically precise,
// just a sensible place to start before fine-tuning by ear with the test tool.
export const VOICE_TYPE_PRESETS = {
  off: [0, 0, 0, 0],
  male: [-6, -2, 3, -1],
  female: [-8, -3, 4, -2],
  mixed: [-6, -2, 3, -1.5],
};

const BAND_LABELS = [
  { name: 'Rumble', hint: 'Room rumble, mic handling noise' },
  { name: 'Boominess', hint: 'Boxy, muddy low-mid tone' },
  { name: 'Clarity', hint: 'Consonant clarity, vocal presence' },
  { name: 'Sibilance', hint: 'Harsh "S" and "T" sounds' },
];

function num(v, fallback) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

// Merges a saved (possibly partial) profile over the defaults, stage by
// stage — used identically wherever a profile is loaded from Firestore.
export function mergeAudioProfile(saved) {
  const savedBands = Array.isArray(saved?.equalizer?.bands) ? saved.equalizer.bands : null;
  return {
    denoise: { ...DEFAULT_AUDIO_PROFILE.denoise, ...(saved?.denoise || {}) },
    highpass: { ...DEFAULT_AUDIO_PROFILE.highpass, ...(saved?.highpass || {}) },
    equalizer: {
      enabled: saved?.equalizer?.enabled ?? DEFAULT_AUDIO_PROFILE.equalizer.enabled,
      bands: DEFAULT_AUDIO_PROFILE.equalizer.bands.map((defaultBand, i) => ({
        ...defaultBand,
        ...(savedBands?.[i] || {}),
      })),
    },
    loudnorm: { ...DEFAULT_AUDIO_PROFILE.loudnorm, ...(saved?.loudnorm || {}) },
    compand: { ...DEFAULT_AUDIO_PROFILE.compand, ...(saved?.compand || {}) },
  };
}

// ── Full set of stage cards + combined test recorder ────────────────────────
// Drop this inside a <fieldset> gated by the caller's own "auto-enhance"
// master switch. `currentUser` is whoever is signed in (used for the test
// recorder's private Storage scratch space) — not necessarily the person the
// profile belongs to, so the Super Admin tool can test-record as the admin
// while editing someone else's saved profile.

export function AudioEnhancementFields({ profile, updateSection, updateEqBand, applyVoicePreset, currentUser }) {
  return (
    <div className="space-y-6">
      <PercentStageCard stageKey="denoise" profile={profile} updateSection={updateSection} />
      <PercentStageCard stageKey="highpass" profile={profile} updateSection={updateSection} />
      <EqualizerStageCard
        profile={profile}
        updateSection={updateSection}
        updateEqBand={updateEqBand}
        applyVoicePreset={applyVoicePreset}
      />
      <PercentStageCard stageKey="loudnorm" profile={profile} updateSection={updateSection} />
      <PercentStageCard stageKey="compand" profile={profile} updateSection={updateSection} />

      {/* One combined test using every stage above exactly as currently
          set — the real, full sound of what this profile would produce. */}
      <div className="card border-2 border-cerulean/30">
        <h3 className="font-semibold text-gray-900 dark:text-white">Test All Settings Together</h3>
        <p className="text-sm text-gray-500 dark:text-white/40 mt-0.5 leading-relaxed">
          Record one test clip and hear it with every setting above applied at once — the same combined
          chain a real recording would go through.
        </p>
        <AudioTestRecorder stageLabel="your settings" profile={profile} currentUser={currentUser} />
      </div>
    </div>
  );
}

// ── Unified stage card ───────────────────────────────────────────────────────
// Every setting gets one card: a preset dropdown up top (the "simple" view),
// and a collapsed "Advanced" disclosure underneath revealing the raw ffmpeg
// parameters that preset resolves to. Both views read and write the exact
// same profile[stageKey] — there's no separate simple/advanced state, so they
// can never disagree. If the raw numbers don't exactly match any preset (e.g.
// someone typed a custom value in Advanced), the dropdown honestly shows
// "Custom" instead of guessing at the nearest stop.

function PercentStageCard({ stageKey, profile, updateSection }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const stage = STAGES[stageKey];
  const params = profile[stageKey];
  const percent = percentForStage(stageKey, params);
  const isCustom = params.enabled && percent === null;

  return (
    <div className="card">
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white">{stage.label}</h3>
        <p className="text-sm text-gray-500 dark:text-white/40 mt-0.5 leading-relaxed">{stage.description}</p>
        {stage.warning && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-medium">⚠ {stage.warning}</p>
        )}
      </div>

      <div className="mt-4">
        <label className="block text-xs font-medium text-gray-500 dark:text-white/40 uppercase tracking-wide mb-1">
          Strength
        </label>
        <select
          className="input-field w-full sm:w-48"
          value={isCustom ? 'custom' : percent}
          onChange={(e) => {
            if (e.target.value === 'custom') return;
            updateSection(stageKey, paramsForStage(stageKey, Number(e.target.value)));
          }}
        >
          <option value={0}>Off</option>
          {INTENSITY_STOPS.map((p) => (
            <option key={p} value={p}>{p}%{p === 35 ? ' (strongest)' : ''}</option>
          ))}
          {isCustom && <option value="custom">Custom (set below)</option>}
        </select>
        <p className="mt-1 text-xs text-gray-400 dark:text-white/35">
          {isCustom
            ? "Doesn't match a preset — this reflects whatever's typed in Advanced below."
            : 'Higher = more aggressive, but more likely to introduce artifacts.'}
        </p>
      </div>

      {params.enabled && (
        <AdvancedDisclosure show={showAdvanced} onToggle={() => setShowAdvanced((v) => !v)}>
          {renderAdvancedFields(stageKey, params, updateSection)}
        </AdvancedDisclosure>
      )}
    </div>
  );
}

function renderAdvancedFields(stageKey, params, updateSection) {
  if (stageKey === 'denoise') {
    return (
      <Field label="Reduction amount (dB)">
        <input
          type="number"
          className="input-field"
          value={params.nr}
          onChange={(e) => updateSection('denoise', { nr: num(e.target.value, DEFAULT_AUDIO_PROFILE.denoise.nr) })}
        />
      </Field>
    );
  }
  if (stageKey === 'highpass') {
    return (
      <Field label="Cutoff frequency (Hz)">
        <input
          type="number"
          className="input-field"
          value={params.freq}
          onChange={(e) => updateSection('highpass', { freq: num(e.target.value, DEFAULT_AUDIO_PROFILE.highpass.freq) })}
        />
      </Field>
    );
  }
  if (stageKey === 'loudnorm') {
    return (
      <div className="grid grid-cols-3 gap-4">
        <Field label="Target loudness (LUFS)">
          <input
            type="number"
            className="input-field"
            value={params.targetLUFS}
            onChange={(e) => updateSection('loudnorm', { targetLUFS: num(e.target.value, DEFAULT_AUDIO_PROFILE.loudnorm.targetLUFS) })}
          />
        </Field>
        <Field label="True peak ceiling (dBTP)">
          <input
            type="number"
            className="input-field"
            value={params.truePeak}
            onChange={(e) => updateSection('loudnorm', { truePeak: num(e.target.value, DEFAULT_AUDIO_PROFILE.loudnorm.truePeak) })}
          />
        </Field>
        <Field label="Loudness range (LRA)">
          <input
            type="number"
            className="input-field"
            value={params.range}
            onChange={(e) => updateSection('loudnorm', { range: num(e.target.value, DEFAULT_AUDIO_PROFILE.loudnorm.range) })}
          />
        </Field>
      </div>
    );
  }
  if (stageKey === 'compand') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Attack time (seconds)" hint="How fast gain reacts to a level increase. Slower = fewer pops, less responsive.">
            <input
              type="number"
              step="0.05"
              className="input-field"
              value={params.attack}
              onChange={(e) => updateSection('compand', { attack: num(e.target.value, DEFAULT_AUDIO_PROFILE.compand.attack) })}
            />
          </Field>
          <Field label="Decay time (seconds)" hint="How fast gain relaxes back down after a level drop.">
            <input
              type="number"
              step="0.05"
              className="input-field"
              value={params.decay}
              onChange={(e) => updateSection('compand', { decay: num(e.target.value, DEFAULT_AUDIO_PROFILE.compand.decay) })}
            />
          </Field>
        </div>
        <Field
          label="Transfer curve (points)"
          hint='ffmpeg compand "points" string — pairs of input-dB/output-dB separated by "|". Editing this directly requires care; a malformed value will fail silently on upload (enhancement is skipped, original audio is kept).'
        >
          <input
            type="text"
            className="input-field font-mono text-sm"
            value={params.points}
            onChange={(e) => updateSection('compand', { points: e.target.value })}
          />
        </Field>
      </div>
    );
  }
  return null;
}

function EqualizerStageCard({ profile, updateSection, updateEqBand, applyVoicePreset }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const bands = profile.equalizer.bands;
  const matchedVoice = Object.entries(VOICE_TYPE_PRESETS).find(
    ([, gains]) => bands.every((b, i) => b.gain === gains[i])
  )?.[0];
  const isCustom = profile.equalizer.enabled && !matchedVoice;

  return (
    <div className="card">
      <SectionHeader
        title="Graphic Equalizer"
        description="Shapes tone across four speech-relevant bands. Pick a voice type for a starting point, then fine-tune in Advanced or by ear with the test tool below."
        enabled={profile.equalizer.enabled}
        onToggle={(v) => updateSection('equalizer', { enabled: v })}
      />
      {profile.equalizer.enabled && (
        <>
          <div className="mt-4">
            <Field label="Voice type" hint="Fills in sensible starting gains for all four bands — fine-tune exact values in Advanced below.">
              <select
                className="input-field w-full sm:w-56"
                value={isCustom ? 'custom' : matchedVoice}
                onChange={(e) => applyVoicePreset(e.target.value)}
              >
                <option value="male">Male voice</option>
                <option value="female">Female voice</option>
                <option value="mixed">Multiple / mixed speakers</option>
                <option value="off">Flat (0dB all bands)</option>
                {isCustom && <option value="custom">Custom (set below)</option>}
              </select>
            </Field>
          </div>

          <AdvancedDisclosure show={showAdvanced} onToggle={() => setShowAdvanced((v) => !v)}>
            <div className="grid sm:grid-cols-2 gap-4">
              {bands.map((band, i) => (
                <div key={i} className="rounded-lg border border-gray-200 dark:border-white/10 p-3">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{BAND_LABELS[i]?.name}</p>
                  <p className="text-xs text-gray-400 dark:text-white/35 mb-2">{BAND_LABELS[i]?.hint}</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label="Freq (Hz)">
                      <input
                        type="number"
                        className="input-field"
                        value={band.freq}
                        onChange={(e) => updateEqBand(i, { freq: num(e.target.value, band.freq) })}
                      />
                    </Field>
                    <Field label="Width (Hz)">
                      <input
                        type="number"
                        className="input-field"
                        value={band.width}
                        onChange={(e) => updateEqBand(i, { width: num(e.target.value, band.width) })}
                      />
                    </Field>
                    <Field label="Gain (dB)">
                      <input
                        type="number"
                        step="0.5"
                        className="input-field"
                        value={band.gain}
                        onChange={(e) => updateEqBand(i, { gain: num(e.target.value, band.gain) })}
                      />
                    </Field>
                  </div>
                </div>
              ))}
            </div>
          </AdvancedDisclosure>
        </>
      )}
    </div>
  );
}

function AdvancedDisclosure({ show, onToggle, children }) {
  return (
    <div className="mt-4 border-t border-gray-100 dark:border-white/10 pt-3">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1 text-xs font-semibold text-cerulean hover:text-cerulean/80 select-none"
      >
        <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${show ? 'rotate-180' : ''}`} />
        Advanced
      </button>
      {show && <div className="mt-3">{children}</div>}
    </div>
  );
}

// ── Test-and-compare recorder ────────────────────────────────────────────────
// Records a short local clip, plays it back raw, then uploads it to the
// signed-in user's own private Storage scratch space and calls the preview
// Cloud Function to hear exactly what the current profile would do to a
// real recording.

function AudioTestRecorder({ stageLabel, profile, currentUser }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [rawURL, setRawURL] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [enhancedURL, setEnhancedURL] = useState(null);
  const [error, setError] = useState('');

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const blobRef = useRef(null);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const anyEnabled = Object.values(profile).some((s) => s.enabled);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (rawURL) URL.revokeObjectURL(rawURL);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset the enhanced preview whenever any setting changes — it no longer
  // reflects the current combination.
  const profileKey = JSON.stringify(profile);
  useEffect(() => {
    setEnhancedURL(null);
  }, [profileKey]);

  const startRecording = async () => {
    setError('');
    setEnhancedURL(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        blobRef.current = blob;
        if (rawURL) URL.revokeObjectURL(rawURL);
        setRawURL(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      recorder.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          if (s >= 4) {
            stopRecording();
            return 5;
          }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      setError('Could not access microphone: ' + (e.message || 'permission denied'));
    }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const hearEnhanced = async () => {
    if (!blobRef.current) return;
    setProcessing(true);
    setError('');
    try {
      const ext = (blobRef.current.type.split('/')[1] || 'webm').split(';')[0];
      const storagePath = `audioEnhancementTests/${currentUser.uid}/combined-${Date.now()}.${ext}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, blobRef.current, { contentType: blobRef.current.type });

      const preview = httpsCallable(functions, 'previewAudioEnhancementStage');
      const result = await preview({ storagePath, profile });
      setEnhancedURL(result.data.audioURL);
    } catch (e) {
      setError(e.message || 'Could not process preview.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="mt-4 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-white/40 mb-2">
        Test this setting
      </p>

      {error && <p className="text-xs text-red-600 dark:text-red-400 mb-2">{error}</p>}

      <div className="flex flex-wrap items-center gap-2">
        {!recording ? (
          <button
            onClick={startRecording}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white dark:bg-white/10 border border-gray-300 dark:border-white/15 hover:border-cerulean hover:text-cerulean text-gray-700 dark:text-white/70 rounded-lg transition-colors"
          >
            <MicrophoneIcon className="w-3.5 h-3.5" />
            Record 5s test clip
          </button>
        ) : (
          <button
            onClick={stopRecording}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-500 text-white rounded-lg animate-pulse"
          >
            <StopIcon className="w-3.5 h-3.5" />
            Recording… {seconds}s
          </button>
        )}

        {rawURL && (
          <>
            <audio controls src={rawURL} className="h-8" style={{ maxWidth: 180 }} />
            <span className="text-xs text-gray-400 dark:text-white/35">raw</span>
          </>
        )}

        {rawURL && !enhancedURL && (
          <button
            onClick={hearEnhanced}
            disabled={processing || !anyEnabled}
            title={!anyEnabled ? 'Turn at least one setting on above to preview it' : ''}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-cerulean/10 hover:bg-cerulean text-cerulean hover:text-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {processing ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-cerulean border-t-transparent rounded-full animate-spin" />
                Processing…
              </>
            ) : (
              <>
                <PlayIcon className="w-3.5 h-3.5" />
                Hear with {stageLabel}
              </>
            )}
          </button>
        )}

        {enhancedURL && (
          <>
            <audio controls src={enhancedURL} className="h-8" style={{ maxWidth: 180 }} />
            <span className="text-xs text-cerulean font-medium">enhanced</span>
          </>
        )}
      </div>
    </div>
  );
}

// ── Small shared pieces ──────────────────────────────────────────────────────

export function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${checked ? 'bg-cerulean' : 'bg-gray-300 dark:bg-white/10'}`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  );
}

function SectionHeader({ title, description, enabled, onToggle }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
        <p className="text-xs text-gray-500 dark:text-white/40 mt-0.5 leading-relaxed">{description}</p>
      </div>
      <Toggle checked={enabled} onChange={onToggle} />
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 dark:text-white/40 uppercase tracking-wide mb-1">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-400 dark:text-white/35">{hint}</p>}
    </div>
  );
}
